/**
 * Form.io UX helpers for the patient-facing forms.
 *
 * When a patient clicks Submit at the bottom of a long form and a required field is
 * incomplete, Form.io renders its error summary + red fields at the TOP of the form —
 * out of view — so it looks like nothing happened. This brings the first error into view
 * (and focuses the offending field) so the patient can see exactly what to fix.
 *
 * Wire it to the `<Form onError={...}>` prop (which maps to Form.io's `error` event,
 * emitted on a failed-validation submit).
 */
/**
 * Return a NEW schema with `disableOnInvalid` cleared on every button. Imported forms set
 * it on Submit, so Form.io disables the button while a required field is empty — and a
 * disabled button swallows the click: no `error` event, no red fields, nothing for
 * scrollToFirstFormError to act on. With it cleared, clicking Submit runs validation,
 * highlights the missing fields and fires `error`. Walks components[], columns[] and
 * rows[][] (like transformFileComponents); input is never mutated.
 */
export function enableSubmitValidation<T>(schema: T): T {
  if (!schema || typeof schema !== 'object') return schema;
  const clone = JSON.parse(JSON.stringify(schema)) as T;
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) { for (const n of node) visit(n); return; }
    if (!node || typeof node !== 'object') return;
    const c = node as Record<string, unknown>;
    if (c['type'] === 'button' && c['disableOnInvalid']) c['disableOnInvalid'] = false;
    if (Array.isArray(c['components'])) visit(c['components']);
    if (Array.isArray(c['columns'])) visit(c['columns']);
    if (Array.isArray(c['rows'])) { for (const r of c['rows'] as unknown[]) visit(r); }
  };
  visit(clone as unknown);
  return clone;
}

export function scrollToFirstFormError(scopeSelector = '.ci-formio-form'): void {
  // Let Form.io finish painting the error state before we look for it.
  window.setTimeout(() => {
    const root = document.querySelector(scopeSelector);
    if (!root) return;
    const el = root.querySelector(
      '.alert-danger, .formio-error, .has-error, .is-invalid, .formio-errors .error',
    );
    if (!(el instanceof HTMLElement)) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // If we landed on a field wrapper, focus its control so the patient is on the field.
    const input = el.matches('input, select, textarea')
      ? el
      : el.querySelector<HTMLElement>('input, select, textarea');
    input?.focus?.({ preventScroll: true });
  }, 80);
}
